import Toast from 'react-native-toast-message';
import { supabase } from './client';
import { getCurrentOrgID } from './organization';
import ErrorHandler from '@/utils/ErrorHandler';
import { isTransientError } from '@/utils/requestQueue';

/**
 * Thin authenticated adapters for the deployed follow_up_* RPCs
 * (reachchms-functions migration 20261027130000_follow_up_touch_rpcs.sql).
 * The server derives organization/actor/isConnected from the JWT — the
 * payload fields below are the only ones the client may send.
 */

/** User-facing copy for the stable FOLLOW_UP_* error contract (RPC DETAIL). */
const FOLLOW_UP_ERROR_MESSAGES = {
    FOLLOW_UP_DENIED: "You don't have permission to do that. Contact your Reach administrator if you think you should.",
    FOLLOW_UP_NOT_FOUND: "That Follow Up record couldn't be found. It may have been removed.",
    FOLLOW_UP_INVALID: 'Something about that Follow Up entry was invalid. Check the details and try again.',
    FOLLOW_UP_CONFLICT: "That Follow Up record can't be changed in its current state.",
};

/** Error carrying the stable contract code for UI-level handling. */
export class FollowUpError extends Error {
    constructor(code, fallbackMessage, original) {
        super(FOLLOW_UP_ERROR_MESSAGES[code] ?? fallbackMessage ?? original?.message ?? 'Something went wrong');
        this.name = 'FollowUpError';
        this.followUpCode = code ?? null;
        this.originalError = original ?? null;
        // Contract/business errors are expected — callers must not treat
        // them as bugs or send them through generic Sentry reporting.
        this.expected = !!code;
    }
}

/** Extracts the FOLLOW_UP_* code from a PostgREST/RPC error (DETAIL field or message). */
export const extractFollowUpCode = (error) => {
    if (!error) return null;
    if (typeof error.details === 'string' && error.details.startsWith('FOLLOW_UP_')) return error.details;
    const match = `${error.details ?? ''} ${error.message ?? ''}`.match(/FOLLOW_UP_[A-Z_]+/);
    return match?.[0] ?? null;
};

/**
 * Shared error path for all follow_up_* calls. Stable FOLLOW_UP_* codes
 * become expected FollowUpErrors with friendly copy (optionally toasted);
 * anything else goes through the standard ErrorHandler/Sentry path and is
 * rethrown so react-query/mutations still observe failure.
 */
const handleFollowUpRpcError = (error, { operation, params = {}, toast = false, friendly } = {}) => {
    const code = extractFollowUpCode(error);
    if (code) {
        const followUpError = new FollowUpError(code, friendly, error);
        if (toast) Toast.show({ type: 'error', text2: followUpError.message });
        return followUpError;
    }

    const wrapped = Error(error.message || 'Follow Up request failed');
    wrapped.code = error.code;
    wrapped.status = error.status;
    wrapped.originalError = error;
    if (isTransientError(error)) {
        // Network blips: let react-query's retry path handle them — no toast.
        return wrapped;
    }
    ErrorHandler(wrapped, { query: operation, ...params, error }, friendly ?? 'Something went wrong in Follow Up');
    return wrapped;
};

const callFollowUpRpc = async (name, params, options = {}) => {
    const { data, error } = await supabase.rpc(name, params);
    if (error) {
        throw handleFollowUpRpcError(error, {
            operation: name,
            // options.params lets callers pass a pre-scrubbed param summary;
            // scrub:false sends nothing. Raw RPC params are only sent when a
            // caller opts in — payloads can carry note narrative/reasons that
            // must never land in Sentry extras (spec §8.6).
            params: options.params ?? (options.scrub === false ? {} : params),
            toast: options.toast,
            friendly: options.friendly,
        });
    }
    return data;
};

/**
 * follow_up_get_person(personID, touchLimit?) — team-read person care surface.
 * Returns { personID, primaryCaregiverPersonID, monitoringMode, monitoredSince,
 * contactWarning, lastPersonalAttemptAt, lastConnectedAt, touches, counts }.
 */
export const getFollowUpPerson = (personID, touchLimit = 50) =>
    callFollowUpRpc('follow_up_get_person', {
        p_person_id: personID,
        p_touch_limit: touchLimit,
    });

/**
 * People the caller is the primary caregiver for. FollowUpPeople is
 * team-readable under RLS — scoped to the caller's personID + org.
 */
export const getMyFollowUpPeople = async ({ organizationID, personID }) => {
    const orgID = organizationID ?? (await getCurrentOrgID());
    if (!orgID || !personID) return [];

    const { data, error } = await supabase
        .from('FollowUpPeople')
        .select(`
            id,
            personID,
            primaryCaregiverPersonID,
            monitoringMode,
            monitoredSince,
            contactWarning,
            Person:People!FollowUpPeople_personID_fkey(
                id, firstName, lastName, avatarStorageKey, avatarUpdatedAt, isArchived
            )
        `)
        .eq('organizationID', orgID)
        .eq('primaryCaregiverPersonID', personID)
        .order('updatedAt', { ascending: false });

    if (error) {
        throw handleFollowUpRpcError(error, {
            operation: 'getMyFollowUpPeople',
            params: { orgID },
            friendly: 'Something went wrong loading your Follow Up people',
        });
    }

    return data ?? [];
};

/**
 * Follow Up team notes for a person: notes with visibility='FOLLOW_UP' or
 * linked to a follow-up touch. RLS already excludes other people's PRIVATE
 * notes; results are deduped client-side by id (a note can match both legs).
 */
export const getFollowUpPersonNotes = async (personID) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase
        .from('PeopleNotes')
        .select(`
            id,
            content,
            isPrivate,
            visibility,
            followUpTouchID,
            taskInstanceID,
            createdAt,
            updatedAt,
            createdBy,
            Author:People!PeopleNotes_createdBy_fkey(
                id, firstName, lastName, avatarStorageKey, avatarUpdatedAt
            )
        `)
        .eq('organizationID', orgID)
        .eq('personID', personID)
        .or('visibility.eq.FOLLOW_UP,followUpTouchID.not.is.null')
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(
            Error(error.message),
            { query: 'getFollowUpPersonNotes', personID, orgID },
            'Something went wrong loading Follow Up notes'
        );
        return [];
    }

    const seen = new Set();
    return (data ?? []).filter((note) => {
        if (seen.has(note.id)) return false;
        seen.add(note.id);
        return true;
    });
};

/**
 * follow_up_log_touch(payload) — the only write that creates a touch.
 * @param {{ personID, type, connectionResult, responseDisposition?, occurredAt?,
 *   taskInstanceID?, note?: { content, visibility? }, idempotencyKey? }} payload
 * Retries with the same idempotencyKey return the original touch (replayed).
 */
export const logFollowUpTouch = (payload) =>
    callFollowUpRpc('follow_up_log_touch', { p_payload: payload }, {
        // Note narrative must never land in Sentry extras (spec §8.6).
        params: {
            personID: payload?.personID,
            type: payload?.type,
            connectionResult: payload?.connectionResult,
            hasNote: !!payload?.note?.content,
        },
        toast: true,
    });

/**
 * follow_up_set_primary_caregiver — manage-only.
 * @param {string|null} caregiverPersonID - null clears the assignment.
 */
export const setPrimaryCaregiver = ({ personID, caregiverPersonID = null, reason = null }) =>
    callFollowUpRpc('follow_up_set_primary_caregiver', {
        p_person_id: personID,
        p_caregiver_person_id: caregiverPersonID,
        p_reason: reason,
    }, { toast: true });

/** follow_up_set_monitoring_mode — manage-only. mode: 'AUTO'|'INCLUDE'|'EXCLUDE'. */
export const setMonitoringMode = ({ personID, mode, reason = null }) =>
    callFollowUpRpc('follow_up_set_monitoring_mode', {
        p_person_id: personID,
        p_mode: mode,
        p_reason: reason,
    }, { toast: true });

/** follow_up_correct_touch — author or manager. patch: {type?, connectionResult?, responseDisposition?, occurredAt?}. */
export const correctFollowUpTouch = ({ touchID, patch }) =>
    callFollowUpRpc('follow_up_correct_touch', {
        p_touch_id: touchID,
        p_patch: patch,
    }, { toast: true });

/** follow_up_retract_touch — author or manager. Marks evidence invalid, never deletes. */
export const retractFollowUpTouch = ({ touchID, reason = null }) =>
    callFollowUpRpc('follow_up_retract_touch', {
        p_touch_id: touchID,
        p_reason: reason,
    }, { toast: true });

/** follow_up_resolve_contact_warning — manage-only. Returns {resolved}. */
export const resolveContactWarning = ({ personID, resolution = null, notes = null }) =>
    callFollowUpRpc('follow_up_resolve_contact_warning', {
        p_person_id: personID,
        p_resolution: resolution,
        p_notes: notes,
    }, {
        // resolution/notes are free text — never send them to Sentry extras.
        params: { personID },
        toast: true,
    });

// ---------------------------------------------------------------------------
// P4 — Tasks integration (follow_up_*_task RPCs). Care tasks are completed
// ONLY through follow_up_complete_task: the server raises
// FOLLOW_UP_COMPLETION_VIA_RPC_REQUIRED on a direct completed=true write.
// ---------------------------------------------------------------------------

/**
 * follow_up_create_task(payload) — team or primary caregiver.
 * Atomically creates the Task definition, TaskInstance, FollowUpTaskContexts
 * row and initial assignment.
 * @param {{ personID, purpose: 'OUTREACH'|'APPROVAL'|'MEAL'|'OTHER', title?,
 *   description?, dueDate?, assigneePersonID?, unassigned?, safeLogistics?,
 *   followUpEnrollmentID?, careEpisodeID?, idempotencyKey? }} payload
 *   careEpisodeID links the ad-hoc task to the person's active care episode —
 *   stored on FollowUpTaskContexts so "Plan a Task" work surfaces under the
 *   episode's plannedTasks (same-org + same-person validated server-side).
 *   followUpEnrollmentID remains the Path-step link for enrollment work.
 * @returns {{taskID, taskInstanceID, assigneePersonID,
 *   followUpEnrollmentID, careEpisodeID, unassigned}}
 */
export const createFollowUpTask = (payload) =>
    callFollowUpRpc('follow_up_create_task', {
        p_payload: payload,
        ...(payload?.followUpEnrollmentID
            ? { p_follow_up_enrollment_id: payload.followUpEnrollmentID }
            : {}),
    }, {
        params: {
            personID: payload?.personID,
            purpose: payload?.purpose,
            unassigned: !!payload?.unassigned,
            linkedToEnrollment: !!payload?.followUpEnrollmentID,
            linkedToEpisode: !!payload?.careEpisodeID,
        },
        toast: true,
    });

/**
 * follow_up_complete_task(payload) — the ONLY completion path for care tasks.
 * OUTREACH requires touch{…}; MEAL → outcome 'DELIVERED';
 * APPROVAL → outcome 'APPROVED'; OTHER → bare or with optional touch.
 * FOLLOW_UP_CONFLICT = already completed/cancelled — callers refetch.
 * @param {{ taskInstanceID, outcome?, touch?: {type, connectionResult,
 *   responseDisposition?, occurredAt?, note?: {content, visibility?}},
 *   idempotencyKey? }} payload
 */
export const completeFollowUpTask = (payload) =>
    callFollowUpRpc('follow_up_complete_task', { p_payload: payload }, {
        params: {
            taskInstanceID: payload?.taskInstanceID,
            outcome: payload?.outcome ?? null,
            hasTouch: !!payload?.touch,
            hasNote: !!payload?.touch?.note?.content,
        },
        toast: true,
    });

/**
 * follow_up_assign_task — manage or primary caregiver of the context person.
 * @returns {{taskInstanceID, assigneePersonID}}
 */
export const assignFollowUpTask = ({ taskInstanceID, assigneePersonID }) =>
    callFollowUpRpc('follow_up_assign_task', {
        p_task_instance_id: taskInstanceID,
        p_assignee_person_id: assigneePersonID,
    }, { toast: true });

/** follow_up_cancel_task — manage or primary caregiver. Returns a summary. */
export const cancelFollowUpTask = ({ taskInstanceID, reason = null }) =>
    callFollowUpRpc('follow_up_cancel_task', {
        p_task_instance_id: taskInstanceID,
        p_reason: reason,
    }, {
        // Cancellation reason is free text — never send it to Sentry extras.
        params: { taskInstanceID },
        toast: true,
    });

/**
 * follow_up_get_assigned_task(taskInstanceID) — helper-safe projection.
 * Callable by an assignee (non-team ok) OR the team.
 * @returns {{taskInstanceID, taskID, title, description, dueDate, purpose,
 *   completed, cancelledAt, personName, safeLogistics, hasContactWarning,
 *   assignees?}} — assignees only present for team callers.
 */
export const getAssignedFollowUpTask = (taskInstanceID) =>
    callFollowUpRpc('follow_up_get_assigned_task', {
        p_task_instance_id: taskInstanceID,
    });

/**
 * Care-context probe for a task instance: RPC projection + the context
 * personID (the FollowUpTaskContexts row is readable by team AND by the
 * instance's assignees under RLS). Returns null when the instance is not a
 * care task OR the caller has no relationship to it — callers treat null as
 * "regular task", never as an error.
 */
export const getFollowUpTaskContext = async (taskInstanceID) => {
    if (!taskInstanceID) return null;

    let task;
    try {
        task = await getAssignedFollowUpTask(taskInstanceID);
    } catch (error) {
        const code = error?.followUpCode ?? extractFollowUpCode(error);
        if (code === 'FOLLOW_UP_NOT_FOUND' || code === 'FOLLOW_UP_DENIED') return null;
        throw error;
    }
    if (!task) return null;

    const { data: context } = await supabase
        .from('FollowUpTaskContexts')
        .select('personID, generationKey')
        .eq('taskInstanceID', taskInstanceID)
        .maybeSingle();

    // §25.7 — a Path-step task carries generationKey 'step:<progressID>'.
    // The context row is readable by the task's assignees, so non-team
    // helpers still detect the Path link; the step's expected channel only
    // resolves for Follow Up team members (FollowUpStepProgress is
    // team-readable) and stays null otherwise — never guessed.
    const stepProgressID = context?.generationKey?.startsWith('step:')
        ? context.generationKey.slice('step:'.length)
        : null;
    let step = null;
    if (stepProgressID) {
        const { data: progress } = await supabase
            .from('FollowUpStepProgress')
            .select('id, actionType, config, status, expiresAt, enrollmentID')
            .eq('id', stepProgressID)
            .maybeSingle();
        if (progress) {
            step = {
                stepProgressID: progress.id,
                enrollmentID: progress.enrollmentID,
                actionType: progress.actionType,
                status: progress.status,
                expiresAt: progress.expiresAt ?? null,
                expectedChannel: progress.config?.channel ?? null,
                instructions: progress.config?.instructions ?? null,
            };
        } else {
            step = { stepProgressID, expectedChannel: null };
        }
    }

    return { ...task, personID: context?.personID ?? null, step };
};

/** follow_up_list_needs_owner — team only. */
export const listFollowUpNeedsOwner = (limit = 50) =>
    callFollowUpRpc('follow_up_list_needs_owner', { p_limit: limit });

/**
 * Care-task instances for a person, for the team care surface and the
 * profile Tasks tab (team viewers only — RLS on FollowUpTaskContexts makes
 * the first leg empty for everyone else).
 *
 * Rows are shaped like getTaskInstancesByTask results so <TaskCard /> can
 * render them directly: {id, taskID, dueDate, completed, cancelledAt, Task,
 * Assignees, followUpContext:{personID, purpose, safeLogistics}}.
 *
 * The context rows are selected directly (team-readable), then each
 * instance is resolved through follow_up_get_assigned_task — the canonical
 * helper-safe projection — rather than relying on a TaskInstances embed,
 * which a team member without the 'tasks' feature could not read under RLS.
 */
export const getFollowUpPersonTasks = async ({ organizationID, personID }) => {
    const orgID = organizationID ?? (await getCurrentOrgID());
    if (!orgID || !personID) return [];

    const { data: contexts, error } = await supabase
        .from('FollowUpTaskContexts')
        .select('taskInstanceID, purpose, safeLogistics')
        .eq('organizationID', orgID)
        .eq('personID', personID);

    if (error) {
        throw handleFollowUpRpcError(error, {
            operation: 'getFollowUpPersonTasks',
            params: { orgID, personID },
            friendly: 'Something went wrong loading Follow Up tasks',
        });
    }
    if (!contexts?.length) return [];

    const tasks = await Promise.all(contexts.map(async (context) => {
        try {
            const task = await getAssignedFollowUpTask(context.taskInstanceID);
            if (!task) return null;
            return {
                id: task.taskInstanceID,
                taskID: task.taskID,
                dueDate: task.dueDate ?? null,
                completed: !!task.completed,
                cancelledAt: task.cancelledAt ?? null,
                isDeleted: false,
                additionalDetails: task.description ?? null,
                Task: { id: task.taskID, title: task.title, description: task.description, dueType: 'FOLLOW_UP' },
                Assignees: (task.assignees || []).map((a) => ({ ...a, personID: a.personID ?? a.id })),
                followUpContext: {
                    personID,
                    purpose: task.purpose ?? context.purpose,
                    safeLogistics: task.safeLogistics ?? context.safeLogistics ?? null,
                },
            };
        } catch (error) {
            const code = error?.followUpCode ?? extractFollowUpCode(error);
            // Context exists but the projection refused — skip the row rather
            // than fail the whole list (denied/non-existent instances are
            // indistinguishable by design).
            if (code === 'FOLLOW_UP_NOT_FOUND' || code === 'FOLLOW_UP_DENIED') return null;
            throw error;
        }
    }));

    return tasks.filter(Boolean).sort((a, b) => {
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aDate - bDate;
    });
};

// ---------------------------------------------------------------------------
// P5 — Monitoring + attention (follow_up_get_overview / _list_people /
// _get_my_follow_up / _update_settings / _upsert_audience / _list_audiences /
// _snooze_attention).
// ---------------------------------------------------------------------------

/**
 * follow_up_get_overview — team only.
 * @returns {{evaluatedAt, needsAttention: [{personID, name, reasons:
 *   [{reason, audienceID, audienceName, firstDetectedAt, snoozed}]}],
 *   needsOwner: [{personID, name, openTaskCount, reason}],
 *   dueWork: [{taskInstanceID, title, personID, personName, dueDate, overdue, purpose}],
 *   recentTouches: [{id, occurredAt, type, connectionResult, isConnected,
 *   responseDisposition, actorName, personID, personName}],
 *   activeCareEpisodes: [{careEpisodeID, personID, personName, firstName,
 *   lastName, avatarStorageKey, avatarUpdatedAt, context, startedAt,
 *   careDetails, primaryCaregiverPersonID, lastTouchAt, openMealSlotCount,
 *   openTaskCount}]}}
 *
 * activeCareEpisodes (§25.9): template-free FollowUpCareEpisodes rows —
 * `context` is the short episode text ("Hospital stay"); `careDetails`
 * carries the §7.5 keys. A null primaryCaregiverPersonID surfaces the
 * Needs Owner state on the card.
 */
export const getFollowUpOverview = ({ touchLimit, attentionLimit } = {}) =>
    callFollowUpRpc('follow_up_get_overview', {
        ...(touchLimit != null ? { p_touch_limit: touchLimit } : {}),
        ...(attentionLimit != null ? { p_attention_limit: attentionLimit } : {}),
    });

/**
 * follow_up_list_people — team only, server-side filtered + cursor paginated.
 * @param {{filter?: {caregiverID?, unassigned?, attentionReason?, monitoringMode?,
 *   hasWarning?}, cursor?, limit?}} args
 * @returns {{people: [...], nextCursor}}
 */
export const listFollowUpPeople = ({ filter, cursor, limit = 50 } = {}) => {
    const payload = { limit };
    if (filter && Object.keys(filter).length > 0) payload.filter = filter;
    if (cursor) payload.cursor = cursor;
    return callFollowUpRpc('follow_up_list_people', { p_payload: payload });
};

/**
 * follow_up_get_my_follow_up — the caller's care surface: people they are the
 * primary caregiver for plus tasks assigned to them (including work for
 * people owned by someone else).
 * @returns {{careFor: [{personID, name, lastAttemptAt, lastConnectedAt,
 *   attentionReasons, hasContactWarning}],
 *   assignedTasks: [{taskInstanceID, title, personID, personName, dueDate,
 *   purpose, overdue}]}}
 */
export const getMyFollowUp = () =>
    callFollowUpRpc('follow_up_get_my_follow_up', {});

/**
 * follow_up_upsert_audience — manage only. Returns the row +
 * {membershipPreview}. Audiences scope WHO is monitored (optional people
 * filter), WHICH events qualify for attendance, and now carry their own
 * attention thresholds (per-audience policy; nullable ints — null = default).
 * filterJSON/filterLogic use the same shape as saved People quick filters
 * (QuickFilters.filterJSON + logic) — send them through like other keys;
 * explicit null clears the filter.
 * @param {{id?, name, filterJSON?, filterLogic?, attendanceEventIDs?,
 *   isActive?, contactAttemptGapDays?, connectedGapDays?, attendanceGapWeeks?}}
 *   payload
 */
export const upsertFollowUpAudience = (payload) =>
    callFollowUpRpc('follow_up_upsert_audience', { p_payload: payload }, {
        params: { id: payload?.id ?? null, isActive: payload?.isActive },
        toast: true,
    });

/**
 * follow_up_delete_audience — manage only. Deletes the audience; FK links
 * (memberships, snoozes) SET NULL so nothing else is silently removed.
 */
export const deleteFollowUpAudience = (audienceID) =>
    callFollowUpRpc('follow_up_delete_audience', { p_audience_id: audienceID }, {
        params: { audienceID },
        friendly: 'Something went wrong deleting that audience',
        toast: true,
    });

/** follow_up_list_audiences — team-readable audience definitions. */
export const listFollowUpAudiences = () =>
    callFollowUpRpc('follow_up_list_audiences', {});

/**
 * follow_up_snooze_attention — manage only. Snoozes ONE reason (optionally
 * scoped to an audience) until a review date — never a blanket "healthy"
 * switch, so snoozing an attendance reason leaves contact/care flags visible
 * (spec §11.6).
 * @param {{personID, reason, audienceID?, until, snoozeReason?}} args —
 *   `until` is an ISO date/timestamp; `snoozeReason` is required by spec.
 */
export const snoozeFollowUpAttention = ({ personID, reason, audienceID = null, until, snoozeReason = null }) =>
    callFollowUpRpc('follow_up_snooze_attention', {
        p_person_id: personID,
        p_reason: reason,
        p_audience_id: audienceID,
        p_until: until,
        p_snooze_reason: snoozeReason,
    }, {
        // snoozeReason is free text — never send it to Sentry extras.
        params: { personID, reason, audienceID },
        toast: true,
    });

// ---------------------------------------------------------------------------
// Flat Paths (§25). Templates publish through follow_up_publish_path (the
// sole write path — direct table writes are revoked); enrollments go
// through follow_up_start_path and stop/resolve lifecycle RPCs. Team-gated
// server-side; publish and step resolution additionally require
// 'Manage Follow Up'. Pause/resume, waivers, approvals, and stage reopen
// are removed contracts — do not reintroduce them.
// ---------------------------------------------------------------------------

/**
 * follow_up_list_paths — team only. Flat definitions + live counts; no
 * kind or stage/requirement counts.
 * @returns [{pathID, name, description, revision, isActive, isArchived,
 *   stepCount, activeEnrollmentCount, createdAt, updatedAt}]
 */
export const listFollowUpPaths = () =>
    callFollowUpRpc('follow_up_list_paths', {});

/**
 * follow_up_get_path(pathID) — team only. The live flat definition plus
 * the revision needed for publish's expectedRevision concurrency check.
 * @returns {pathID, name, description, revision, isActive, isArchived,
 *   steps: [{stepID, actionType, label, instructions, sortOrder, waitDays,
 *   expiresAfterDays, config, poolMembers: [{personID, name, position}]}],
 *   activeEnrollmentCount, latestRevision: {revision, publishedAt, summary}}
 */
export const getFollowUpPath = (pathID) =>
    callFollowUpRpc('follow_up_get_path', { p_path_id: pathID });

/**
 * follow_up_publish_path(payload) — manage only. Atomic create/update of
 * the whole flat definition: validates every step, bumps revision, audits
 * a revision row, and reconciles active enrollments in-transaction
 * (finished work keeps its outcome; unstarted removed steps retire).
 * FOLLOW_UP_CONFLICT = stale expectedRevision (callers refetch + re-offer).
 * @param {{pathID?, expectedRevision?, name, description?, isActive?,
 *   isArchived?, steps: [{stepID?, actionType, label?, instructions?,
 *   sortOrder?, waitDays, expiresAfterDays?, config?}]}} payload
 */
export const publishFollowUpPath = (payload) =>
    callFollowUpRpc('follow_up_publish_path', { p_payload: payload }, {
        params: {
            pathID: payload?.pathID ?? null,
            expectedRevision: payload?.expectedRevision ?? null,
            stepCount: payload?.steps?.length ?? 0,
        },
        toast: true,
    });

/**
 * follow_up_start_path(personID, pathID, idempotencyKey?) — team only.
 * Idempotent: a second start on an already-open enrollment returns
 * {alreadyOpen: true, enrollmentID} instead of erroring — callers navigate
 * to the existing enrollment rather than surfacing an error. A replayed
 * idempotency key returns the ORIGINAL enrollment even after it stopped.
 * No kind/careDetails — the RPC rejects them (Care uses follow_up_start_care).
 * @returns {enrollmentID, pathID, personID, status?, alreadyOpen, replayed?}
 */
export const startFollowUpPath = ({ personID, pathID, idempotencyKey = null }) =>
    callFollowUpRpc('follow_up_start_path', {
        p_person_id: personID,
        p_path_id: pathID,
        p_idempotency_key: idempotencyKey,
    }, {
        params: { personID, pathID },
        toast: true,
    });

/**
 * follow_up_list_enrollments(payload) — team only, cursor paginated
 * (keyset on startedAt DESC, id DESC; pass the previous page's
 * nextCursor).
 * @param {{filter?: {personID?, pathID?, status?: 'ACTIVE'|'COMPLETED'|
 *   'CANCELLED'|'ALL', assigneePersonID?, needsAttention?, search?},
 *   cursor?, limit?}} args
 * @returns {rows: [{enrollmentID, personID, personName, pathID, pathName,
 *   status, startedAt, completedAt, cancelledAt, stopReason, stoppedAt,
 *   stoppedByPersonID, currentStep: {stepProgressID, label, actionType,
 *   status, availableAt, expiresAt, attentionReason, assigneePersonID,
 *   assigneeName} | null, needsAttention, progress: {effective,
 *   progressed, successful, expired, skipped}}], nextCursor}
 */
export const listFollowUpEnrollments = ({ filter, cursor, limit = 50 } = {}) => {
    const payload = { limit };
    if (filter && Object.keys(filter).length > 0) payload.filter = filter;
    if (cursor) payload.cursor = cursor;
    return callFollowUpRpc('follow_up_list_enrollments', { p_payload: payload });
};

/**
 * follow_up_get_enrollment(enrollmentID) — team only. Flat step timeline
 * with truthful per-step outcomes (actual performer — never implied
 * assignee), structured stop metadata, and §25.8 shared-denominator
 * progress counts.
 * @returns {enrollment: {enrollmentID, personID, personName, pathID,
 *   pathName, pathRevision, status, startedAt, startedByPersonID,
 *   completedAt, cancelledAt, cancelledByPersonID, cancellationReason,
 *   stopReason, stoppedAt, stoppedByPersonID, stopTouchID},
 *   steps: [{stepProgressID, stepID, sortOrder, actionType, label,
 *   instructions, config, status, availableAt, activatedAt, expiresAt,
 *   resolvedAt, taskInstanceID, touchID, performerPersonID, performerName,
 *   outcomeReason, skipReason, attentionReason, stepArchived,
 *   assignees: [{personID, name, assignmentResponse}]}],
 *   progress: {effective, progressed, successful, expired, skipped,
 *   cancelled}}
 */
export const getFollowUpEnrollment = (enrollmentID) =>
    callFollowUpRpc('follow_up_get_enrollment', { p_enrollment_id: enrollmentID });

/**
 * follow_up_stop_enrollment(enrollmentID, reason?, stopTouchID?) — manage
 * OR the person's primary caregiver. Canonical Stop Path (§25.6):
 * atomically cancels the enrollment's unfinished work (open step Tasks,
 * waiting/pending progress, undispatched effect claims) and stamps
 * structured stop metadata. A restart is a NEW enrollment, never a resume.
 * FOLLOW_UP_CONFLICT = the enrollment is already terminal.
 * @returns {enrollmentID, status: 'CANCELLED', stopReason,
 *   stoppedByPersonID, cancelledStepCount, cancelledTaskCount}
 */
export const stopFollowUpEnrollment = ({ enrollmentID, reason = null, stopTouchID = null }) =>
    callFollowUpRpc('follow_up_stop_enrollment', {
        p_enrollment_id: enrollmentID,
        p_reason: reason,
        p_stop_touch_id: stopTouchID,
    }, {
        params: { enrollmentID },
        toast: true,
    });

/**
 * follow_up_resolve_step(progressID, action, reason?) — manage only.
 * 'SKIP' intentionally skips a WAITING/ACTIVE/NEEDS_ATTENTION step
 * (cancels its open Task) and advances once; 'RETRY' re-arms a
 * NEEDS_ATTENTION step — never a blind resend of an in-flight send.
 * Terminal steps and stopped/finished enrollments are FOLLOW_UP_CONFLICT.
 * @returns step progress summary
 */
export const resolveFollowUpStep = ({ stepProgressID, action, reason = null }) =>
    callFollowUpRpc('follow_up_resolve_step', {
        p_progress_id: stepProgressID,
        p_action: action,
        p_reason: reason,
    }, {
        // The skip reason is free text — never send it to Sentry extras.
        params: { stepProgressID, action },
        toast: true,
    });

// ---------------------------------------------------------------------------
// Template-free Care (§25.9). A FollowUpCareEpisodes row IS the care
// episode — no Path/template, lifecycle ACTIVE → CLOSED by explicit act
// only. Slot status is OPEN/CANCELLED (the need); delivered state derives
// from the linked task's completed + deliveredAt — claimed is never
// delivered (C10). Notify pushes are transient outbox rows, never a saved
// recipient list or ownership grant.
// ---------------------------------------------------------------------------

/**
 * follow_up_start_care(payload) — Follow Up team OR the person's primary
 * caregiver. One ACTIVE episode per (org, person): a repeat/concurrent
 * start returns the same episode with alreadyOpen:true and never re-sends
 * start pushes. No recipients / no registered device / opt-out never blocks
 * intake and never warns (§25.9 — dashboard visibility is the fallback).
 * @param {{personID, context?, careDetails?, notifyPersonIDs?,
 *   idempotencyKey?}} payload
 *   context = optional short episode text ("Hospital stay") — not a
 *   template. notifyPersonIDs = same-org people pushed ONCE via durable
 *   FOLLOW_UP_CARE_PUSH outbox rows; selection creates no ownership and no
 *   saved recipient list.
 * @returns {careEpisodeID, personID, status, needsOwner,
 *   notifiedPersonIDs, alreadyOpen, replayed?}
 */
export const startFollowUpCare = (payload) =>
    callFollowUpRpc('follow_up_start_care', { p_payload: payload }, {
        // Context/details/notify are user narrative — ids only to Sentry.
        params: {
            personID: payload?.personID,
            hasContext: !!payload?.context,
            detailKeys: payload?.careDetails ? Object.keys(payload.careDetails) : [],
            notifyCount: payload?.notifyPersonIDs?.length ?? 0,
        },
        toast: true,
    });

/**
 * follow_up_get_care_episode(careEpisodeID) — team or primary caregiver.
 * The episode row + person + primary caregiver + care details + recent
 * episode touches + open planned tasks + meal slots with staffing/delivery
 * state. No Path stages, no pathID.
 * @returns {episode: {careEpisodeID, personID, personName, context,
 *   careDetails, status, startedAt, startedByPersonID, closedAt,
 *   closedByPersonID, closureNote}, primaryCaregiver: {personID, name}|null,
 *   recentTouches: [...], plannedTasks: [...], mealSlots: [...]}
 */
export const getFollowUpCareEpisode = (careEpisodeID) =>
    callFollowUpRpc('follow_up_get_care_episode', { p_care_episode_id: careEpisodeID });

/**
 * follow_up_list_care_episodes(payload) — team only. Permission-scoped
 * active-care dashboard summary (§25.9): person, context, caregiver/
 * Needs-owner state and light activity metadata — detailed medical/
 * logistics text stays off the summary. Keyset on startedAt DESC; pass the
 * previous page's nextCursor.
 * @param {{status?: 'ACTIVE'|'CLOSED'|'CANCELLED'|'ALL', limit?, cursor?}}
 * @returns {rows: [{careEpisodeID, personID, personName, firstName,
 *   lastName, avatarStorageKey, avatarUpdatedAt, context, status,
 *   startedAt, primaryCaregiverPersonID, lastTouchAt, openMealSlotCount,
 *   openTaskCount}], nextCursor}
 */
export const listFollowUpCareEpisodes = ({ status, cursor, limit = 50 } = {}) => {
    const payload = {};
    if (status) payload.status = status;
    if (cursor) payload.cursor = cursor;
    if (limit != null) payload.limit = limit;
    return callFollowUpRpc('follow_up_list_care_episodes', { p_payload: payload });
};

/**
 * A person's ACTIVE care episode, for the person Follow Up surface. Reads
 * FollowUpCareEpisodes directly — RLS allows the Follow Up team OR the
 * person's primary caregiver, the same gate as follow_up_get_care_episode.
 * Returns the row (id/personID/context/status/startedAt) or null.
 */
export const getFollowUpPersonActiveCareEpisode = async ({ organizationID, personID }) => {
    const orgID = organizationID ?? (await getCurrentOrgID());
    if (!orgID || !personID) return null;

    const { data, error } = await supabase
        .from('FollowUpCareEpisodes')
        .select('id, personID, context, status, startedAt')
        .eq('organizationID', orgID)
        .eq('personID', personID)
        .eq('status', 'ACTIVE')
        .maybeSingle();

    if (error) {
        throw handleFollowUpRpcError(error, {
            operation: 'getFollowUpPersonActiveCareEpisode',
            params: { orgID, personID },
            friendly: 'Something went wrong loading active care',
        });
    }
    return data;
};

/**
 * follow_up_set_meal_dates(payload) — manage or the person's primary
 * caregiver. Enable/edit/disable dated meal assistance on an ACTIVE care
 * episode (spec §13.4–13.5/§25.9). One needed meal per date; resubmission
 * is idempotent (per-date generation keys — C05).
 * @param {{careEpisodeID, enabled?: boolean, dates?: string[],
 *   deliveryInstructions?, deliveryWindow?, preview?: boolean,
 *   confirmImpact?: boolean}} payload
 *   dates = 'YYYY-MM-DD' strings — required non-empty when enabling (C04).
 *   preview:true never mutates; confirmImpact is required to APPLY a change
 *   that cancels assigned or delivered slots.
 * @returns applied:true → {careEpisodeID, enabled, applied, added,
 *   existingDatesKept, cancelled, kept};
 *   preview → {careEpisodeID, enabled, applied:false, preview:true,
 *   requiresConfirmation, desiredDates, existingDatesKept,
 *   impact: [{mealSlotID, mealDate, taskInstanceID, assigneePersonID,
 *   delivered, action: 'cancel_slot_keep_delivered'|'cancel_assigned'|
 *   'cancel_unclaimed'}]} — the impact rows the UI must show before
 *   resubmitting with confirmImpact (never silently erase commitments).
 */
export const setFollowUpMealDates = (payload) =>
    callFollowUpRpc('follow_up_set_meal_dates', { p_payload: payload }, {
        params: {
            careEpisodeID: payload?.careEpisodeID,
            enabled: payload?.enabled ?? null,
            dateCount: payload?.dates?.length ?? 0,
            preview: !!payload?.preview,
            confirmImpact: !!payload?.confirmImpact,
        },
        toast: true,
    });

/**
 * follow_up_claim_meal(mealSlotID, idempotencyKey?) — Follow Up team only.
 * Capacity-one self-claim; the TaskInstancePeople row is the authoritative
 * assignment. FOLLOW_UP_CONFLICT means someone else won the claim race
 * (C06/C07) — callers refetch and show the staffed state, never optimistic.
 * Claimed is NOT delivered: the response carries delivered:false.
 * @returns {mealSlotID, taskInstanceID, assigneePersonID, claimed:true,
 *   delivered:false, alreadyClaimed?, replayed?}
 */
export const claimFollowUpMeal = ({ mealSlotID, idempotencyKey = null }) =>
    callFollowUpRpc('follow_up_claim_meal', {
        p_meal_slot_id: mealSlotID,
        p_idempotency_key: idempotencyKey,
    }, {
        params: { mealSlotID },
        toast: true,
    });

/**
 * follow_up_update_care_details(careEpisodeID, patch) — manage or the
 * person's primary caregiver (the same gate as follow_up_close_care).
 * RFC 7396 merge-patch on FollowUpCareEpisodes.careDetails: send ONLY the
 * keys that changed; each supplied key sets/replaces the stored value and
 * a top-level JSON null deletes the key. Nested values replace wholesale.
 * Only ACTIVE episodes accept edits — CLOSED/CANCELLED episodes are
 * retained history and reject as FOLLOW_UP_INVALID.
 * @param {{careEpisodeID: string, patch: object}} args — build the patch
 *   with buildCareDetailsPatch so cleared fields serialize as nulls.
 *   '{}' is a valid no-op.
 * @returns {careEpisodeID, careDetails, updatedAt} — the merged result.
 */
export const updateFollowUpCareDetails = ({ careEpisodeID, patch }) =>
    callFollowUpRpc('follow_up_update_care_details', {
        p_care_episode_id: careEpisodeID,
        p_care_details: patch ?? {},
    }, {
        // Care details are free text — only the touched key names reach
        // Sentry extras, never the values (spec §8.6).
        params: { careEpisodeID, keys: patch ? Object.keys(patch) : [] },
        toast: true,
    });

/**
 * follow_up_close_care(payload) — manage or the person's primary caregiver.
 * Explicit closure of an ACTIVE care episode (spec §13.6/§25.9): never
 * auto-completes, never silently deletes work. preview:true returns
 * pendingWork without closing — the "review before close" surface; the same
 * list is echoed on apply so the response always carries decision-time
 * state.
 * @param {{careEpisodeID, closureNote?, cancelTaskInstanceIDs?,
 *   keepTaskInstanceIDs?, preview?: boolean, idempotencyKey?}} payload
 *   Unlisted pending work is kept by default (§13.6).
 * @returns preview → {careEpisodeID, preview:true, closed:false,
 *   pendingWork: [{taskInstanceID, title, purpose, dueDate, mealSlotID,
 *   mealDate, assignees: [{personID, name}]}]};
 *   apply → {careEpisodeID, status:'CLOSED', closed:true, closedAt,
 *   closedByPersonID, noteID, pendingWork, cancelledTaskInstanceIDs,
 *   keptTaskInstanceIDs, skippedTaskDecisions: [{taskInstanceID,
 *   skipped: 'completed'|'cancelled'|...}]}
 *   skippedTaskDecisions covers work that completed/cancelled between
 *   preview and apply — completion is retained, never cancelled (C13).
 */
export const closeFollowUpCare = (payload) =>
    callFollowUpRpc('follow_up_close_care', { p_payload: payload }, {
        // The closure note is free text — never send it to Sentry extras.
        params: {
            careEpisodeID: payload?.careEpisodeID,
            preview: !!payload?.preview,
            cancelCount: payload?.cancelTaskInstanceIDs?.length ?? 0,
            keepCount: payload?.keepTaskInstanceIDs?.length ?? 0,
        },
        toast: !payload?.preview,
    });
